import React, { useCallback, useRef, useState } from 'react';
import { Form } from 'react-bootstrap';

type EditableCellProps = {
  /** Initial value shown in the input when activated */
  initialValue: string;
  /** Optional read-only badge prefix (e.g. "#CHOICE ") — prepended on commit */
  badgePrefix?: string;
  /** CSS classes for the wrapping <td> */
  tdClassName: string;
  /** Called with the final value (badgePrefix + edited text) on Enter / blur */
  onCommit: (value: string) => void;
  /** Called on Escape — parent should deactivate the cell */
  onCancel: () => void;
  /** Whether to render as badge+input (partial) or plain input */
  hasBadge?: boolean;
};

/**
 * Isolated editable cell component.
 * Keeps `editValue` state local so typing only re-renders this component,
 * not the entire TableEditor tree.
 *
 * Focus: uses `autoFocus` prop on Form.Control — browser focuses synchronously
 * on mount so no keystrokes are lost while waiting for a setTimeout.
 *
 * Escape: calls e.stopPropagation() so the event does not bubble to the Modal
 * and close it instead of cancelling the edit.
 */
export const EditableCell = React.memo(function EditableCell({
  initialValue,
  badgePrefix,
  tdClassName,
  onCommit,
  onCancel,
  hasBadge = false,
}: EditableCellProps) {
  const [editValue, setEditValue] = useState(initialValue);
  const committedRef = useRef(false);

  const commit = useCallback(() => {
    if (committedRef.current) return;
    committedRef.current = true;
    let fullValue: string;
    if (badgePrefix) {
      fullValue = editValue ? `${badgePrefix}${editValue}` : badgePrefix.trimEnd();
    } else {
      fullValue = editValue;
    }
    onCommit(fullValue);
  }, [editValue, badgePrefix, onCommit]);

  const handleKeyDown = useCallback((e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      commit();
    } else if (e.key === 'Escape') {
      e.preventDefault();
      e.stopPropagation(); // prevent Modal from closing instead of cancelling edit
      onCancel();
    }
  }, [commit, onCancel]);

  if (hasBadge && badgePrefix) {
    return (
      <td className={tdClassName}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
          <span
            style={{
              display: 'inline-block',
              fontSize: 11,
              lineHeight: 1,
              color: '#3b3f45',
              background: '#e9ecef',
              borderRadius: 999,
              padding: '3px 7px',
              marginRight: 4,
              userSelect: 'none',
            }}
          >
            {badgePrefix.trim()}
          </span>
          <Form.Control
            // eslint-disable-next-line jsx-a11y/no-autofocus
            autoFocus
            value={editValue}
            onChange={(e) => setEditValue(e.target.value)}
            onBlur={commit}
            onKeyDown={handleKeyDown}
            style={{ flex: 1 }}
          />
        </div>
      </td>
    );
  }

  return (
    <td className={tdClassName}>
      <Form.Control
        // eslint-disable-next-line jsx-a11y/no-autofocus
        autoFocus
        value={editValue}
        onChange={(e) => setEditValue(e.target.value)}
        onBlur={commit}
        onKeyDown={handleKeyDown}
      />
    </td>
  );
});
